import { useRef, useEffect, useState } from "react";
import type { MinimapEvent } from "./MinimapView";

const MAGNIFIER_WIDTH = 250;
const MAGNIFIER_HEIGHT = 250;
const EVENTS_TO_SHOW = 5; // Show 5 events in magnifier

interface MagnifierWindowProps {
  position: { x: number; y: number };
  events: MinimapEvent[];
  centerEventIndex: number;
  side: 'left' | 'right';
}

export function MagnifierWindow({ position, events, centerEventIndex, side }: MagnifierWindowProps) {
  const magnifierRef = useRef<HTMLDivElement>(null);
  const [adjustedPosition, setAdjustedPosition] = useState(position);

  useEffect(() => {
    if (!magnifierRef.current) return;

    const magnifierRect = magnifierRef.current.getBoundingClientRect();
    const sandboxPane = magnifierRef.current.closest('.sandbox-content');
    if (!sandboxPane) return;

    const sandboxRect = sandboxPane.getBoundingClientRect();

    let x = position.x;
    let y = position.y;

    // Position magnifier on the SAME side as minimap
    // If minimap is on left, magnifier stays on left
    // If minimap is on right, magnifier stays on right
    if (side === 'left') {
      x = sandboxRect.left + 10; // Stick to left edge
    } else {
      x = sandboxRect.right - MAGNIFIER_WIDTH - 10; // Stick to right edge
    }

    // Keep within sandbox bounds (already handled above)
    if (x + MAGNIFIER_WIDTH > sandboxRect.right) {
      x = sandboxRect.right - MAGNIFIER_WIDTH - 10;
    }
    if (x < sandboxRect.left) {
      x = sandboxRect.left + 10;
    }

    // Vertically center on cursor, but keep in bounds
    y = position.y - MAGNIFIER_HEIGHT / 2;
    if (y + MAGNIFIER_HEIGHT > sandboxRect.bottom) {
      y = sandboxRect.bottom - MAGNIFIER_HEIGHT - 10;
    }
    if (y < sandboxRect.top) {
      y = sandboxRect.top + 10;
    }

    setAdjustedPosition({ x, y });
  }, [position, side]);

  // Get events to display (centered around hovered event)
  const startIndex = Math.max(0, centerEventIndex - Math.floor(EVENTS_TO_SHOW / 2));
  const endIndex = Math.min(events.length, startIndex + EVENTS_TO_SHOW);
  const visibleEvents = events.slice(startIndex, endIndex);

  return (
    <div
      ref={magnifierRef}
      className="fixed z-50 bg-white dark:bg-gray-800 border-2 border-blue-500 rounded-lg shadow-2xl overflow-hidden pointer-events-none"
      style={{
        left: `${adjustedPosition.x}px`,
        top: `${adjustedPosition.y}px`,
        width: `${MAGNIFIER_WIDTH}px`,
        height: `${MAGNIFIER_HEIGHT}px`,
      }}
    >
      {/* Magnifier Header */}
      <div className="bg-blue-500 text-white px-2 py-1 text-xs font-semibold">
        Magnifier View
      </div>

      {/* Event List */}
      <div className="p-2 space-y-2 overflow-hidden h-full">
        {visibleEvents.map((event, index) => {
          const isCenter = startIndex + index === centerEventIndex;
          return (
            <div
              key={event.id}
              className={`p-2 rounded text-xs ${
                isCenter 
                  ? 'bg-blue-100 dark:bg-blue-900 border-2 border-blue-500' 
                  : 'bg-gray-50 dark:bg-gray-700'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${getEventBadgeColor(event.type)}`}>
                  {event.type.toUpperCase()}
                </span>
                <span className="text-[10px] text-gray-500 dark:text-gray-400">
                  {new Date(event.timestamp).toLocaleTimeString()}
                </span>
              </div>
              
              {event.participantName && (
                <div className="text-[10px] font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {event.participantName}
                </div>
              )}
              
              {event.content && (
                <div className="text-[11px] text-gray-600 dark:text-gray-400">
                  {event.content}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getEventBadgeColor(type: MinimapEvent['type']): string {
  const colors: Record<MinimapEvent['type'], string> = {
    message: 'bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200',
    system: 'bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-200',
    join: 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200',
    leave: 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200',
    typing: 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200',
    reaction: 'bg-purple-200 text-purple-800 dark:bg-purple-800 dark:text-purple-200',
  };
  return colors[type];
}

